import { COMPANY_URL } from '@src/routes/company';
import { TENANT_URL } from '@src/routes/tenant';
import logger from '@src/utils/logger';
import axios, { AxiosRequestConfig } from 'axios';

import { ApiResult } from './apiResult';

const ENDPOINT_URL = import.meta.env.VITE_BACKEND_API_URL || '';
export const API_BASE_URL = ENDPOINT_URL + '/v1';

const buildUrl = (path: string): string => {
  return API_BASE_URL + path;
};

const axiosInstance = axios.create();
axiosInstance.defaults.withCredentials = true;
// axios 1.14以降、withCredentials: true はXSRFトークン送信をトリガーしなくなったため明示指定
axiosInstance.defaults.withXSRFToken = true;

export const axiosClient = {
  async get<SUCCESS, ERROR>(
    path: string,
    config?: AxiosRequestConfig<unknown>,
  ): Promise<ApiResult<SUCCESS, ERROR>> {
    try {
      const res = await axiosInstance.get<SUCCESS>(buildUrl(path), config);
      return {
        result: 'success',
        data: res.data,
      };
    } catch (error) {
      return handleException<SUCCESS, ERROR>(error);
    }
  },
  async post<SUCCESS, ERROR>(
    path: string,
    params: unknown,
    config?: AxiosRequestConfig<FormData>,
  ): Promise<ApiResult<SUCCESS, ERROR>> {
    try {
      const res = await axiosInstance.post<SUCCESS>(buildUrl(path), params, config);

      return {
        result: 'success',
        data: res.data,
      };
    } catch (error) {
      return handleException<SUCCESS, ERROR>(error);
    }
  },
  async put<SUCCESS, ERROR>(
    path: string,
    params: unknown,
    config?: AxiosRequestConfig<FormData>,
  ): Promise<ApiResult<SUCCESS, ERROR>> {
    try {
      const res = await axiosInstance.put<SUCCESS>(buildUrl(path), params, config);
      return {
        result: 'success',
        data: res.data,
      };
    } catch (error) {
      return handleException<SUCCESS, ERROR>(error);
    }
  },
  async delete<SUCCESS, ERROR>(
    path: string,
    config?: AxiosRequestConfig<unknown>,
  ): Promise<ApiResult<SUCCESS, ERROR>> {
    try {
      const res = await axiosInstance.delete<SUCCESS>(buildUrl(path), config);
      return {
        result: 'success',
        data: res.data,
      };
    } catch (error) {
      return handleException<SUCCESS, ERROR>(error);
    }
  },
};

const handleException = <_, ERROR>(error: unknown): ApiResult<_, ERROR> => {
  if (axios.isAxiosError(error)) {
    if (error.code === 'ERR_CANCELED') {
      return {
        result: 'aborted',
        message: 'Request was aborted',
      };
    }
    if (error.response) {
      if ([401, 403].includes(error.response.status)) {
        return {
          result: 'unauthorized',
          message: 'Unauthorized',
        };
      }
      if ([413].includes(error.response.status)) {
        return {
          result: 'validationError',
          message: 'Request Entity Too Large Error',
          errors: error.response.statusText as ERROR,
        };
      }
      if ([422].includes(error.response.status)) {
        return {
          result: 'validationError',
          message: error.response.data.message,
          errors: error.response.data.errors || error.response.data,
        };
      }
      if ([429].includes(error.response.status)) {
        return {
          result: 'tooManyRequestsError',
          message: error.response.data.message,
        };
      }
      if (error.response.status >= 400 && error.response.status < 500) {
        return {
          result: 'notFoundError',
          message: error.response.data.message,
        };
      }
    }
  }

  // ここに到達するのは 5xx / ネットワーク / 想定外エラー（SSR では render(500) を招くケース）。
  // SSR(front-node サーバー)で発生した場合に原因を CloudWatch で追えるよう、エラー内容をログ出力する。
  if (axios.isAxiosError(error)) {
    logger.error('[API exception]', {
      method: error.config?.method,
      url: error.config?.url,
      status: error.response?.status ?? null,
      message: error.message,
    });
  } else {
    logger.error('[API exception] non-axios error', error);
  }

  return {
    result: 'exception',
    message: '予期せぬエラーが発生しました。',
  };
};

const errorStatusHandlers: Array<(status: number | null) => void> = [];
export const setErrorStatus = (setError: (status: number | null) => void) => {
  errorStatusHandlers.push(setError);
};

const notifyErrorStatus = (status: number | null) => {
  errorStatusHandlers.forEach((handler) => handler(status));
};

export const createAbortableConfig = (signal?: AbortSignal): AxiosRequestConfig => ({
  signal,
});

export const isAbortError = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.code === 'ERR_CANCELED';
};

export const handleApiResponse = <SUCCESS, VALIDATION_ERROR>(
  response: ApiResult<SUCCESS, VALIDATION_ERROR>,
  onSuccess: (data: SUCCESS) => void,
  onValidationError?: (errors: VALIDATION_ERROR) => void,
  // 一覧取得など「ページ自体の404」を意味しない呼び出しで、
  // グローバル404/500/429への切り替えを避けて呼び出し元でエラー処理したい場合に指定する
  onError?: (response: ApiResult<SUCCESS, VALIDATION_ERROR>) => void,
) => {
  switch (response.result) {
    case 'success':
      onSuccess(response.data);
      break;
    case 'aborted':
      // Abortの場合は成功として握りつぶす
      break;
    case 'validationError':
      if (onValidationError) {
        onValidationError(response.errors);
      }
      break;
    case 'exception':
      console.error(response.message);
      if (onError) {
        onError(response);
      } else {
        notifyErrorStatus(500);
      }
      break;
    case 'unauthorized':
      console.error(response.message);

      if (window.location.pathname.startsWith('/tenant')) {
        window.location.href = TENANT_URL.LOGIN;
      } else {
        window.location.href = COMPANY_URL.LOGIN;
      }
      break;
    case 'tooManyRequestsError':
      console.error(response.message);
      if (onError) {
        onError(response);
      } else {
        notifyErrorStatus(429);
      }
      break;
    case 'notFoundError':
      console.error(response.message);
      if (onError) {
        onError(response);
      } else {
        notifyErrorStatus(404);
      }
      break;
  }
};
